import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, ChevronDown } from 'lucide-react';

// Brand Assets URLs
const ASSETS = {
  logo: 'https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/ub6fc5ad_WhatsApp%20Image%202026-03-26%20at%208.22.14%20PM%20%281%29.jpeg',
  hero: 'https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/263uegs4_WhatsApp%20Image%202026-03-26%20at%208.24.32%20PM.jpeg',
  jorge: 'https://customer-assets.emergentagent.com/job_85e44fd7-710b-462a-910e-af3fcb279b6f/artifacts/xjnbdtpb_WhatsApp%20Image%202026-03-26%20at%2010.25.31%20PM.jpeg',
  flavia: 'https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/9ge5z3cp_WhatsApp%20Image%202026-03-26%20at%208.24.32%20PM%20%281%29.jpeg',
  tableEvent: 'https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/efpoo0al_WhatsApp%20Image%202026-03-26%20at%208.33.21%20PM%20%281%29.jpeg',
  socialEvent: 'https://customer-assets.emergentagent.com/job_destiny-match-9/artifacts/qo24s45v_WhatsApp%20Image%202026-03-26%20at%208.33.21%20PM.jpeg',
  // Flags
  flagMX: 'https://flagcdn.com/w40/mx.png',
  flagUS: 'https://flagcdn.com/w40/us.png',
  flagBR: 'https://flagcdn.com/w40/br.png'
};

export { ASSETS };

// Language Selector Component
const LanguageSelector = () => {
  const { language, setLanguage, languages } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const flags = {
    es: ASSETS.flagMX,
    en: ASSETS.flagUS,
    pt: ASSETS.flagBR
  };

  const currentLang = languages.find(l => l.code === language);

  return (
    <div className="language-selector relative">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-[#8B1A1A]/30 hover:border-[#8B1A1A] transition-colors"
        data-testid="language-selector"
      >
        <img src={flags[language]} alt={currentLang?.name} className="flag-icon" />
        <ChevronDown size={14} className={`text-[#8A8A8A] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="language-selector-dropdown mt-2 z-50">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setIsOpen(false); }}
                className={`language-option w-full ${language === lang.code ? 'active' : ''}`}
                data-testid={`lang-${lang.code}`}
              >
                <img src={flags[lang.code]} alt={lang.name} className="flag-icon" />
                <span className="text-sm">{lang.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Logo Component
const Logo = ({ className = "h-12 w-auto" }) => (
  <Link to="/" className="flex items-center gap-3 group">
    <img 
      src={ASSETS.logo} 
      alt="Soul Mate Dates"
      className={`${className} logo-glow object-contain transition-transform duration-500 group-hover:scale-105`}
    />
  </Link>
);

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'nav-glass py-4' : 'bg-transparent py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Logo className="h-12 w-auto" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {/* Language Selector */}
            <LanguageSelector />

            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase"
                >
                  {t('nav_dashboard')}
                </Link>
                {['admin', 'super_admin'].includes(user?.role) && (
                  <Link 
                    to="/admin" 
                    className="text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase"
                  >
                    {t('nav_admin')}
                  </Link>
                )}
                {['admin', 'super_admin'].includes(user?.role) && (
                  <Link 
                    to="/lab" 
                    className="text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase"
                    data-testid="nav-lab-link"
                  >
                    LAB
                  </Link>
                )}
                <button
                  onClick={logout}
                  className="text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase"
                >
                  {t('nav_logout')}
                </button>
                <div className="flex items-center gap-3 pl-6 border-l border-[#8B1A1A]/20">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name}
                      className="w-10 h-10 rounded-full border border-[#8B1A1A]/50"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#161616] border border-[#8B1A1A]/50 flex items-center justify-center">
                      <span className="text-[#8B1A1A] text-sm font-medium">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase"
                >
                  {t('nav_login')}
                </Link>
                <Link 
                  to="/apply" 
                  className="btn-wine text-sm px-8 py-3"
                  data-testid="apply-button"
                >
                  {t('nav_apply')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-4">
            <LanguageSelector />
            <button 
              className="text-[#F5F0E6] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-[#0B0B0B] border-t border-[#8B1A1A]/10">
          <div className="px-6 py-6 space-y-4">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="block text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav_dashboard')}
                </Link>
                {['admin', 'super_admin'].includes(user?.role) && (
                  <Link 
                    to="/admin" 
                    className="block text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav_admin')}
                  </Link>
                )}
                {['admin', 'super_admin'].includes(user?.role) && (
                  <Link 
                    to="/lab" 
                    className="block text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    LAB
                  </Link>
                )}
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="block text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase py-2 w-full text-left"
                >
                  {t('nav_logout')}
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="block text-[#8A8A8A] hover:text-[#8B1A1A] transition-colors text-sm tracking-widest uppercase py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav_login')}
                </Link>
                <Link 
                  to="/apply" 
                  className="block btn-wine text-center text-sm py-4"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav_apply')}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
export { Logo, LanguageSelector };
